import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import Divider from "@mui/material/Divider";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PrintIcon from "@mui/icons-material/Print";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import {
  ETaxDocTypeGroup,
  ETaxSummaryCard,
  ETaxSummaryResponse,
} from "../../types/e-tax.type";
import { formatNumber, numberWithCommas } from "../../utils";
import { DOC_TYPE_CHIP_COLOR } from "./documentMeta";
import { AmountRow, LoadErrorState } from "./documentCells";

interface SummaryCardsProps {
  summary?: ETaxSummaryResponse;
  loading: boolean;
  onViewDetail: () => void;
  onViewHistory: () => void;
  onSend: () => void;
  // the card degrades to a disabled button if a caller omits the handler.
  onViewPaper?: () => void;
  /** The summary request failed: the cards say so and offer a retry instead of ไม่พบข้อมูล. */
  error?: boolean;
  onRetry?: () => void;
}

const TypeCountChip = ({
  type,
  count,
}: {
  type: ETaxDocTypeGroup;
  count: number;
}) => {
  const style = DOC_TYPE_CHIP_COLOR[type];
  return (
    <Chip
      label={`${type}: ${count}`}
      size="small"
      sx={{ color: style.color, bgcolor: style.bg, fontWeight: 500 }}
    />
  );
};

// Rendered heights of the Typography variants the loaded card body uses. The skeleton
// reserves exactly these, so the card is already its final size while loading and the
// row of cards does not grow when the figures arrive.
const LABEL_HEIGHT = 20;
const NET_TOTAL_HEIGHT = 32;
const AMOUNT_HEIGHT = 28;
const TYPE_CHIP_HEIGHT = 24;

/** One label + value pair of the loading card, mirroring an {@link AmountRow}. */
const SkeletonAmountRow = ({ valueHeight }: { valueHeight: number }) => (
  <Box sx={{ mb: 1.5 }}>
    <Skeleton variant="text" width="45%" height={LABEL_HEIGHT} />
    <Skeleton variant="text" width="60%" height={valueHeight} />
  </Box>
);

/**
 * Stands in for the loaded card body block for block: net total, the two amounts, the
 * document count and the type chips, dividers included.
 */
const SummaryCardSkeleton = () => (
  <Box sx={{ flexGrow: 1 }}>
    <SkeletonAmountRow valueHeight={NET_TOTAL_HEIGHT} />
    <Divider sx={{ mb: 1.5 }} />
    <SkeletonAmountRow valueHeight={AMOUNT_HEIGHT} />
    <SkeletonAmountRow valueHeight={AMOUNT_HEIGHT} />
    <Divider sx={{ mb: 1.5 }} />
    <SkeletonAmountRow valueHeight={AMOUNT_HEIGHT} />
    <Box display="flex" gap={1}>
      {[0, 1, 2].map((index) => (
        <Skeleton
          key={index}
          variant="rounded"
          width={64}
          height={TYPE_CHIP_HEIGHT}
        />
      ))}
    </Box>
  </Box>
);

interface CardProps {
  title: string;
  icon: React.ReactNode;
  iconBg: string;
  data?: ETaxSummaryCard;
  loading: boolean;
  action: React.ReactNode;
  error?: boolean;
  onRetry?: () => void;
}

const SummaryCard = ({
  title,
  icon,
  iconBg,
  data,
  loading,
  action,
  error,
  onRetry,
}: CardProps) => (
  <Paper
    sx={{
      height: "100%",
      display: "flex",
      flexDirection: "column",
      p: 2.5,
    }}
  >
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="flex-start"
      mb={2}
    >
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          bgcolor: iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
    </Box>

    {loading ? (
      <SummaryCardSkeleton />
    ) : !data ? (
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 180,
        }}
      >
        {error && onRetry ? (
          <LoadErrorState onRetry={onRetry} />
        ) : (
          <Typography color="text.secondary" fontWeight={500}>
            ไม่พบข้อมูล
          </Typography>
        )}
      </Box>
    ) : (
      <Box sx={{ flexGrow: 1 }}>
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            มูลค่ารวมสุทธิ (Net Total)
          </Typography>
          <Box display="flex" alignItems="baseline" gap={0.5}>
            <Typography variant="h5" fontWeight={700}>
              {formatNumber(data.net_total)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              บาท
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        <AmountRow label="มูลค่าก่อน VAT" value={`${formatNumber(data.amount_before_vat)} บาท`} />
        <AmountRow label="ภาษีมูลค่าเพิ่ม" value={`${formatNumber(data.vat_amount)} บาท`} />

        <Divider sx={{ mb: 1.5 }} />

        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary">
            จำนวนเอกสารรวม
          </Typography>
          <Box display="flex" alignItems="baseline" gap={0.5}>
            <Typography variant="subtitle1" fontWeight={700}>
              {numberWithCommas(data.document_count)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              ใบ
            </Typography>
          </Box>
        </Box>

        <Box display="flex" gap={1} flexWrap="wrap">
          <TypeCountChip type="INV" count={data.type_counts.INV} />
          <TypeCountChip type="DN" count={data.type_counts.DN} />
          <TypeCountChip type="CN" count={data.type_counts.CN} />
        </Box>
      </Box>
    )}

    <Box display="flex" justifyContent="flex-end" mt={2}>
      {action}
    </Box>
  </Paper>
);

export default function SummaryCards({
  summary,
  loading,
  onViewDetail,
  onViewHistory,
  onSend,
  onViewPaper,
  error,
  onRetry,
}: SummaryCardsProps) {
  const errorProps = { error, onRetry };
  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, md: 3 }}>
        <SummaryCard
          title="เอกสารทั้งหมด"
          icon={<DescriptionIcon sx={{ color: "#5C69E8" }} />}
          iconBg="#ECEEFD"
          data={summary?.all}
          loading={loading}
          {...errorProps}
          action={
            <Button variant="outlined" onClick={onViewDetail}>
              ดูรายละเอียดรายการ
            </Button>
          }
        />
      </Grid>

      <Grid size={{ xs: 12, md: 3 }}>
        <SummaryCard
          title="ส่งแบบกระดาษ"
          icon={<ReceiptLongIcon sx={{ color: "#64748B" }} />}
          iconBg="#EEF1F5"
          data={summary?.paper}
          loading={loading}
          {...errorProps}
          action={
            <Button variant="outlined" onClick={onViewPaper} disabled={!onViewPaper}>
              ดูรายละเอียดรายการ
            </Button>
          }
        />
      </Grid>

      <Grid size={{ xs: 12, md: 3 }}>
        <SummaryCard
          title="ส่ง E-Tax แล้ว"
          icon={<CheckCircleIcon sx={{ color: "#45BE77" }} />}
          iconBg="#E7F7EE"
          data={summary?.sent}
          loading={loading}
          {...errorProps}
          action={
            <Button variant="outlined" onClick={onViewHistory}>
              ดูประวัติการส่ง
            </Button>
          }
        />
      </Grid>

      <Grid size={{ xs: 12, md: 3 }}>
        <SummaryCard
          title="ค้างส่ง E-Tax"
          icon={<PrintIcon sx={{ color: "#FDA42C" }} />}
          iconBg="#FFF3E3"
          data={summary?.pending}
          loading={loading}
          {...errorProps}
          action={
            <Button
              variant="contained"
              onClick={onSend}
              disabled={!summary || summary.pending.document_count === 0}
            >
              ส่งเอกสาร E-Tax
            </Button>
          }
        />
      </Grid>
    </Grid>
  );
}
